(() => {
  const initialized = new WeakSet();
  const profilePriority = ['A', 'C', 'D', 'B'];

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function initQuiz(quiz) {
    if (!quiz || initialized.has(quiz)) return;
    initialized.add(quiz);

    const screens = Array.from(quiz.querySelectorAll('[data-fq-screen]'));
    const questions = Array.from(quiz.querySelectorAll('[data-fq-question]'));
    const progress = quiz.querySelector('[data-fq-progress]');
    const progressCopy = quiz.querySelector('[data-fq-progress-copy]');
    const progressMarkers = Array.from(quiz.querySelectorAll('[data-fq-progress-marker]'));
    const sideStep = quiz.querySelector('[data-fq-side-step]');
    const visualNote = quiz.querySelector('[data-fq-visual-note]');
    const initialVisualNote = visualNote?.textContent || '';
    const leadForm = quiz.querySelector('.fq-lead-form');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const storageKey = `femilabs-quiz-result:${quiz.id || window.location.pathname}`;

    let currentQuestion = -1;

    const label = (key, fallback) => quiz.dataset[key] || fallback;

    function setScreen(name, focus = true) {
      screens.forEach((screen) => {
        const active = screen.dataset.fqScreen === name;
        screen.hidden = !active;
        screen.classList.toggle('is-active', active);
      });

      if (focus) {
        const activeScreen = screens.find((screen) => screen.dataset.fqScreen === name);
        const heading = activeScreen?.querySelector('h1, h2');
        window.requestAnimationFrame(() => heading?.focus({ preventScroll: true }));
      }

      updateProgress(name);
      quiz.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    }

    function updateProgress(screenName = 'question') {
      const count = questions.length;
      let completed = 0;
      let copy = quiz.querySelector('[data-fq-progress-copy]')?.textContent || '';

      if (screenName === 'intro') {
        copy = progressCopy?.dataset.initial || progressCopy?.textContent || '';
      } else if (screenName === 'question') {
        completed = currentQuestion + 1;
        copy = `${label('progressStepLabel', 'Стъпка')} ${completed} ${label('progressOfLabel', 'от')} ${count}`;
      } else if (screenName === 'lead' || screenName === 'analysis') {
        completed = count;
        copy = label('progressFinalLabel', 'Финална стъпка');
      } else if (screenName === 'result') {
        completed = count;
        copy = label('progressResultLabel', 'Твоят резултат');
      }

      if (progressCopy) progressCopy.textContent = copy;
      if (progress) progress.setAttribute('aria-valuenow', String(completed));

      const filledMarkers = count ? Math.ceil((completed / count) * progressMarkers.length) : 0;
      progressMarkers.forEach((marker, index) => {
        marker.classList.toggle('is-complete', index < filledMarkers);
        marker.classList.toggle('is-current', completed > 0 && index === Math.max(0, filledMarkers - 1) && screenName === 'question');
      });

      if (sideStep) {
        const number = screenName === 'intro' ? 0 : Math.min(count, Math.max(1, completed));
        sideStep.textContent = `${String(number).padStart(2, '0')} / ${String(count).padStart(2, '0')}`;
      }
    }

    if (progressCopy) progressCopy.dataset.initial = progressCopy.textContent;

    function selectedInputs(question) {
      return Array.from(question.querySelectorAll('[data-fq-option]:checked'));
    }

    function syncContinue(question) {
      const button = question.querySelector('[data-fq-continue]');
      if (button) button.disabled = selectedInputs(question).length === 0;
    }

    function showQuestion(index) {
      currentQuestion = clamp(index, 0, questions.length - 1);
      screens.forEach((screen) => {
        const active = screen === questions[currentQuestion];
        screen.hidden = !active;
        screen.classList.toggle('is-active', active);
      });

      const question = questions[currentQuestion];
      syncContinue(question);
      updateProgress('question');

      if (visualNote) {
        visualNote.textContent = question.dataset.visualNote || initialVisualNote;
      }

      window.requestAnimationFrame(() => question.querySelector('h2')?.focus({ preventScroll: true }));
      quiz.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    }

    function goForward() {
      if (currentQuestion < questions.length - 1) {
        showQuestion(currentQuestion + 1);
      } else {
        prepareLeadData();
        setScreen('lead');
      }
    }

    function goBack() {
      const activeScreen = screens.find((screen) => !screen.hidden);
      if (activeScreen?.dataset.fqScreen === 'lead') {
        showQuestion(currentQuestion);
        return;
      }
      if (currentQuestion > 0) {
        showQuestion(currentQuestion - 1);
      } else if (currentQuestion === 0) {
        currentQuestion = -1;
        if (visualNote) visualNote.textContent = initialVisualNote;
        setScreen('intro');
      } else {
        showQuestion(questions.length - 1);
      }
    }

    function readResult() {
      const scores = { A: 0, B: 0, C: 0, D: 0 };
      const resultValues = {};
      const answerLines = [];
      let nutrientPoints = 0;
      questions.forEach((question, index) => {
        const selected = selectedInputs(question);
        const values = selected.map((input) => input.value);
        if (values.length) {
          answerLines.push(`${index + 1}. ${question.querySelector('h2')?.textContent?.trim() || ''}: ${values.join(', ')}`);
        }

        const resultKey = question.dataset.resultKey;
        if (resultKey && values.length) resultValues[resultKey] = values;

        selected.forEach((input) => {
          String(input.dataset.score || '').split(',').forEach((token) => {
            const [rawKey, rawValue] = token.split(':').map((part) => part?.trim());
            const value = Number(rawValue);
            if (!rawKey || Number.isNaN(value)) return;
            if (Object.prototype.hasOwnProperty.call(scores, rawKey)) scores[rawKey] += value;
            if (rawKey === 'N') nutrientPoints += value;
          });
        });
      });

      let avatar = quiz.dataset.resultFallback || 'B';
      let highest = 0;
      profilePriority.forEach((key) => {
        if (scores[key] > highest) {
          highest = scores[key];
          avatar = key;
        }
      });

      const base = Number(quiz.dataset.nutrientBase || 52);
      const step = Number(quiz.dataset.nutrientStep || 10);
      const nutrient = clamp(base + nutrientPoints * step, 20, 95);

      return { scores, resultValues, answerLines, avatar, highest, nutrient };
    }

    function prepareLeadData() {
      if (!leadForm) return;
      const result = readResult();
      const tags = leadForm.querySelector('[data-fq-lead-tags]');
      const note = leadForm.querySelector('[data-fq-lead-note]');
      if (tags) tags.value = `newsletter, quiz, femihair, femihair-avatar-${result.avatar}`;
      if (note) note.value = [`FemiHair quiz profile: ${result.avatar}`, ...result.answerLines].join('\n');
    }

    function savePendingResult() {
      const payload = {
        savedAt: Date.now(),
        answers: questions.map((question) => selectedInputs(question).map((input) => input.value))
      };

      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(payload));
      } catch (storageError) {
        // The customer form can still submit when browser storage is unavailable.
      }
    }

    function restorePendingResult() {
      try {
        const stored = window.sessionStorage.getItem(storageKey);
        if (!stored) return false;

        const payload = JSON.parse(stored);
        const isFresh = Number(payload.savedAt) > Date.now() - 30 * 60 * 1000;
        if (!isFresh || !Array.isArray(payload.answers)) {
          window.sessionStorage.removeItem(storageKey);
          return false;
        }

        questions.forEach((question, questionIndex) => {
          const values = Array.isArray(payload.answers[questionIndex]) ? payload.answers[questionIndex] : [];
          question.querySelectorAll('[data-fq-option]').forEach((input) => {
            input.checked = values.includes(input.value);
          });
          syncContinue(question);
        });

        return true;
      } catch (storageError) {
        return false;
      }
    }

    function showSubmittedResult() {
      const params = new URLSearchParams(window.location.search);
      if (params.get('customer_posted') !== 'true' || !restorePendingResult()) return false;

      setScreen('analysis');
      const delay = reducedMotion.matches ? 250 : Number(quiz.dataset.analysisDelay || 1600);
      window.setTimeout(() => {
        renderResult();
        try {
          window.sessionStorage.removeItem(storageKey);
        } catch (storageError) {
          // Nothing else is required after rendering the restored result.
        }
      }, delay);

      window.history.replaceState({}, '', `${window.location.pathname}${window.location.hash}`);
      return true;
    }

    function renderResult() {
      const result = readResult();
      const profiles = Array.from(quiz.querySelectorAll('[data-fq-result-profile]'));
      let activeProfile = profiles.find((profile) => profile.dataset.avatar === result.avatar);
      if (!activeProfile) activeProfile = profiles[0];

      profiles.forEach((profile) => {
        profile.hidden = profile !== activeProfile;
      });

      const baseMatch = Number(activeProfile?.dataset.matchBase || 82);
      const match = clamp(baseMatch + Math.max(0, result.highest - 2) * 2, 60, 97);
      const matchTarget = activeProfile?.querySelector('[data-fq-result-match]');
      if (matchTarget) matchTarget.textContent = String(match);

      const nutrientTarget = quiz.querySelector('[data-fq-result-nutrient]');
      const nutrientMeter = quiz.querySelector('[data-fq-result-meter]');
      if (nutrientTarget) nutrientTarget.textContent = String(result.nutrient);
      if (nutrientMeter) window.requestAnimationFrame(() => nutrientMeter.style.width = `${result.nutrient}%`);

      const durationTarget = quiz.querySelector('[data-fq-result-duration]');
      if (durationTarget) durationTarget.textContent = result.resultValues.duration?.join(', ') || 'Не е посочено';

      const goals = result.resultValues.goals || [];
      const goalsWrap = quiz.querySelector('[data-fq-result-goals]');
      const goalsList = quiz.querySelector('[data-fq-result-goal-list]');
      if (goalsWrap && goalsList) {
        goalsWrap.hidden = goals.length === 0;
        goalsList.replaceChildren(...goals.map((goal) => {
          const item = document.createElement('span');
          item.textContent = goal;
          return item;
        }));
      }

      setScreen('result');
      activeProfile?.querySelector('h2')?.focus({ preventScroll: true });
    }

    function restart() {
      questions.forEach((question) => {
        question.querySelectorAll('[data-fq-option]').forEach((input) => { input.checked = false; });
        syncContinue(question);
      });
      leadForm?.reset();
      currentQuestion = -1;
      if (visualNote) visualNote.textContent = initialVisualNote;
      setScreen('intro');
    }

    quiz.addEventListener('click', (event) => {
      if (event.target.closest('[data-fq-start]')) {
        showQuestion(0);
        return;
      }

      if (event.target.closest('[data-fq-back]')) {
        goBack();
        return;
      }

      if (event.target.closest('[data-fq-continue]')) {
        const question = questions[currentQuestion];
        if (question && selectedInputs(question).length) goForward();
        return;
      }

      if (event.target.closest('[data-fq-restart]')) restart();
    });

    quiz.addEventListener('change', (event) => {
      const input = event.target.closest('[data-fq-option]');
      if (!input) return;
      const question = input.closest('[data-fq-question]');
      if (!question) return;
      syncContinue(question);

    });

    leadForm?.addEventListener('submit', (event) => {
      const error = leadForm.querySelector('[data-fq-form-error]');
      if (!leadForm.reportValidity()) {
        event.preventDefault();
        return;
      }

      prepareLeadData();
      savePendingResult();
      if (error) error.hidden = true;
    });

    if (!showSubmittedResult()) updateProgress('intro');
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-fq]').forEach(initQuiz);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    const quiz = block.closest('[data-fq]');
    if (!quiz || !block.matches('[data-fq-question]')) return;
    const questions = Array.from(quiz.querySelectorAll('[data-fq-question]'));
    questions.forEach((question) => { question.hidden = question !== block; });
    quiz.querySelectorAll('[data-fq-screen]:not([data-fq-question])').forEach((screen) => { screen.hidden = true; });
  });
})();
